import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useCompany } from '../../context/CompanyContext';
import type { Quotation, Party, SalesRep } from '../../types';
import {
  exportQuotationsCSV,
  exportQuotationsExcel,
  exportFinancialsCSV,
  exportFinancialsExcel,
  exportAnalyticsCSV,
  exportAnalyticsExcel,
  type FinancialReportData,
  type AnalyticsReportData,
  type FinancialBranchStats,
  type MonthlyFinancialStats,
  type SalesRepAnalytics,
  type TopPartyAnalytics,
  type StatusFunnelAnalytics,
} from '../../lib/reportsExporter';

export default function ReportsPage() {
  const { companies, selectedCompanyId, setSelectedCompanyId } = useCompany();

  const [activeTab, setActiveTab] = useState<'quotations' | 'financial' | 'analytics'>('quotations');
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [reps, setReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, []);

  async function fetchReportData() {
    setLoading(true);
    try {
      const [qRes, pRes, rRes] = await Promise.all([
        supabase.from('quotations').select('*').order('quotation_date', { ascending: false }),
        supabase.from('parties').select('id, name, address1, address3'),
        supabase.from('sales_reps').select('id, code, name, role'),
      ]);

      setQuotations(qRes.data || []);
      setParties(pRes.data || []);
      setReps(rRes.data || []);
    } catch (e) {
      console.error('Error fetching report data:', e);
    } finally {
      setLoading(false);
    }
  }

  // Filtered quotations based on company switcher, status filter, and search
  const filteredQuotations = useMemo(() => {
    return quotations.filter((q) => {
      const matchStatus = statusFilter === 'all' || q.status === statusFilter;
      const matchCompany = selectedCompanyId === 'all' || q.company_id === selectedCompanyId;
      if (!matchStatus || !matchCompany) return false;

      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const party = parties.find((p) => p.id === q.party_id);
      const company = companies.find((c) => c.id === q.company_id);
      const rep = reps.find((r) => r.id === q.rep_id);

      return (
        (q.quotation_number || '').toLowerCase().includes(query) ||
        (q.query_number || '').toLowerCase().includes(query) ||
        (party?.name || '').toLowerCase().includes(query) ||
        (company?.code || '').toLowerCase().includes(query) ||
        (rep?.name || '').toLowerCase().includes(query)
      );
    });
  }, [quotations, statusFilter, selectedCompanyId, searchQuery, parties, companies, reps]);

  // ─── Financial Dataset Computations ───
  const financialData: FinancialReportData = useMemo(() => {
    const totalValue = filteredQuotations.reduce((sum, q) => sum + Number(q.total || 0), 0);
    const totalSubtotal = filteredQuotations.reduce((sum, q) => sum + Number(q.subtotal || 0), 0);
    const totalDiscount = filteredQuotations.reduce((sum, q) => sum + Number(q.discount_amount || 0), 0);
    const totalPF = filteredQuotations.reduce((sum, q) => sum + Number(q.pf_amount || 0), 0);
    const totalGST = filteredQuotations.reduce((sum, q) => sum + Number(q.gst_amount || 0), 0);
    const approvedQuotes = filteredQuotations.filter((q) => q.status === 'approved');
    const approvedValue = approvedQuotes.reduce((sum, q) => sum + Number(q.total || 0), 0);

    const branches: FinancialBranchStats[] = companies.map((c) => {
      const quotes = filteredQuotations.filter((q) => q.company_id === c.id);
      const grossSubtotal = quotes.reduce((sum, q) => sum + Number(q.subtotal || 0), 0);
      const discountAmount = quotes.reduce((sum, q) => sum + Number(q.discount_amount || 0), 0);
      const pfAmount = quotes.reduce((sum, q) => sum + Number(q.pf_amount || 0), 0);
      const gstAmount = quotes.reduce((sum, q) => sum + Number(q.gst_amount || 0), 0);
      const netTotal = quotes.reduce((sum, q) => sum + Number(q.total || 0), 0);
      const avgTicket = quotes.length > 0 ? Math.round(netTotal / quotes.length) : 0;
      const sharePct = totalValue > 0 ? (netTotal / totalValue) * 100 : 0;

      return {
        id: c.id,
        code: c.code,
        name: c.name,
        count: quotes.length,
        grossSubtotal,
        discountAmount,
        pfAmount,
        gstAmount,
        netTotal,
        avgTicket,
        sharePct,
      };
    }).filter((b) => b.count > 0).sort((a, b) => b.netTotal - a.netTotal);

    // Monthly Distribution (last 6 months)
    const monthMap: Record<string, { count: number; pipeline: number; approved: number; gst: number }> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
      monthMap[key] = { count: 0, pipeline: 0, approved: 0, gst: 0 };
    }

    filteredQuotations.forEach((q) => {
      if (q.quotation_date) {
        const d = new Date(q.quotation_date);
        const key = d.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
        if (monthMap[key]) {
          monthMap[key].count++;
          monthMap[key].pipeline += Number(q.total || 0);
          if (q.status === 'approved') {
            monthMap[key].approved += Number(q.total || 0);
          }
          monthMap[key].gst += Number(q.gst_amount || 0);
        }
      }
    });

    const monthly: MonthlyFinancialStats[] = Object.entries(monthMap).map(([month, data]) => ({
      month,
      count: data.count,
      pipelineAmount: Math.round(data.pipeline),
      approvedAmount: Math.round(data.approved),
      gstAmount: Math.round(data.gst),
    }));

    return {
      totalValue,
      totalSubtotal,
      totalDiscount,
      totalPF,
      totalGST,
      approvedValue,
      approvedCount: approvedQuotes.length,
      branches,
      monthly,
    };
  }, [filteredQuotations, companies]);

  // ─── Analytics Dataset Computations ───
  const analyticsData: AnalyticsReportData = useMemo(() => {
    const totalValue = financialData.totalValue;
    const totalQuotes = filteredQuotations.length;

    // Reps leaderboard
    const repStats: SalesRepAnalytics[] = reps.map((r) => {
      const repQuotes = filteredQuotations.filter((q) => q.rep_id === r.id);
      const draftQuotes = repQuotes.filter((q) => !q.status || q.status === 'draft');
      const sentQuotes = repQuotes.filter((q) => q.status === 'sent');
      const appQuotes = repQuotes.filter((q) => q.status === 'approved');
      const rejQuotes = repQuotes.filter((q) => q.status === 'rejected');
      const totalAmount = repQuotes.reduce((sum, q) => sum + Number(q.total || 0), 0);
      const approvedAmount = appQuotes.reduce((sum, q) => sum + Number(q.total || 0), 0);
      const conversionRate = repQuotes.length > 0 ? (appQuotes.length / repQuotes.length) * 100 : 0;

      return {
        id: r.id,
        code: r.code,
        name: r.name,
        role: r.role,
        count: repQuotes.length,
        draftCount: draftQuotes.length,
        sentCount: sentQuotes.length,
        approvedCount: appQuotes.length,
        rejectedCount: rejQuotes.length,
        totalAmount,
        approvedAmount,
        conversionRate,
      };
    }).filter((r) => r.count > 0).sort((a, b) => b.totalAmount - a.totalAmount);

    // Top parties
    const partyMap = new Map<number, { name: string; count: number; amount: number }>();
    filteredQuotations.forEach((q) => {
      if (!q.party_id) return;
      const existing = partyMap.get(q.party_id) || {
        name: parties.find((p) => p.id === q.party_id)?.name || 'Unknown',
        count: 0,
        amount: 0,
      };
      partyMap.set(q.party_id, {
        name: existing.name,
        count: existing.count + 1,
        amount: existing.amount + Number(q.total || 0),
      });
    });

    const topParties: TopPartyAnalytics[] = Array.from(partyMap.entries())
      .map(([id, p]) => ({
        id,
        rank: 1,
        name: p.name,
        count: p.count,
        amount: p.amount,
        avgTicket: p.count > 0 ? Math.round(p.amount / p.count) : 0,
        sharePct: totalValue > 0 ? (p.amount / totalValue) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10)
      .map((p, idx) => ({ ...p, rank: idx + 1 }));

    // Status Funnel
    const statusCounts: Record<string, { count: number; amount: number }> = {
      draft: { count: 0, amount: 0 },
      sent: { count: 0, amount: 0 },
      approved: { count: 0, amount: 0 },
      rejected: { count: 0, amount: 0 },
    };

    filteredQuotations.forEach((q) => {
      const s = (q.status || 'draft').toLowerCase();
      if (s in statusCounts) {
        statusCounts[s].count++;
        statusCounts[s].amount += Number(q.total || 0);
      }
    });

    const funnel: StatusFunnelAnalytics[] = Object.entries(statusCounts).map(([status, item]) => ({
      status,
      count: item.count,
      amount: item.amount,
      percentage: totalQuotes > 0 ? (item.count / totalQuotes) * 100 : 0,
    }));

    return {
      reps: repStats,
      parties: topParties,
      funnel,
      totalValue,
      totalQuotes,
    };
  }, [filteredQuotations, reps, parties, financialData.totalValue]);

  // ─── Export Triggers ───
  async function handleExportQuotations(type: 'csv' | 'excel') {
    try {
      setExporting(true);
      if (type === 'csv') {
        exportQuotationsCSV(filteredQuotations, parties, companies, reps);
      } else {
        await exportQuotationsExcel(filteredQuotations, parties, companies, reps);
      }
    } finally {
      setExporting(false);
    }
  }

  async function handleExportFinancials(type: 'csv' | 'excel') {
    try {
      setExporting(true);
      if (type === 'csv') {
        exportFinancialsCSV(financialData);
      } else {
        await exportFinancialsExcel(financialData);
      }
    } finally {
      setExporting(false);
    }
  }

  async function handleExportAnalytics(type: 'csv' | 'excel') {
    try {
      setExporting(true);
      if (type === 'csv') {
        exportAnalyticsCSV(analyticsData);
      } else {
        await exportAnalyticsExcel(analyticsData);
      }
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* ═══ Header Bar & Export Actions ═══ */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Commercial Intelligence</span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900 mt-1">
            Reports & Commercial Analytics
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Audit quotations, branch financial statements, sales rep leaderboards, and tax breakdowns
          </p>
        </div>

        {/* Dynamic Action Buttons based on Active Tab */}
        <div className="flex flex-wrap items-center gap-2">
          {activeTab === 'quotations' && (
            <>
              <button
                onClick={() => handleExportQuotations('csv')}
                disabled={exporting || filteredQuotations.length === 0}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3.5 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>📥</span> Export CSV
              </button>
              <button
                onClick={() => handleExportQuotations('excel')}
                disabled={exporting || filteredQuotations.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>📊</span> Export Excel
              </button>
            </>
          )}

          {activeTab === 'financial' && (
            <>
              <button
                onClick={() => handleExportFinancials('csv')}
                disabled={exporting}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3.5 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>📥</span> Export Financials CSV
              </button>
              <button
                onClick={() => handleExportFinancials('excel')}
                disabled={exporting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>📊</span> Export Financial Statement (Excel)
              </button>
            </>
          )}

          {activeTab === 'analytics' && (
            <>
              <button
                onClick={() => handleExportAnalytics('csv')}
                disabled={exporting}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3.5 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>📥</span> Export Analytics CSV
              </button>
              <button
                onClick={() => handleExportAnalytics('excel')}
                disabled={exporting}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <span>📈</span> Export Executive Analytics (Excel)
              </button>
            </>
          )}
        </div>
      </div>

      {/* ═══ Navigation Tabs ═══ */}
      <div className="flex border-b border-slate-200 bg-white px-4 rounded-xl border">
        <button
          onClick={() => setActiveTab('quotations')}
          className={`py-3.5 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-2 ${
            activeTab === 'quotations'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <span>📄</span>
          <span>Quotations Report</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
            {filteredQuotations.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('financial')}
          className={`py-3.5 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-2 ${
            activeTab === 'financial'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <span>💰</span>
          <span>Financial Reports</span>
        </button>

        <button
          onClick={() => setActiveTab('analytics')}
          className={`py-3.5 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-2 ${
            activeTab === 'analytics'
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <span>📈</span>
          <span>Analytics & Leaderboard</span>
        </button>
      </div>

      {loading ? (
        <div className="p-16 text-center text-slate-500 space-y-3 bg-white rounded-2xl border border-slate-200">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
          <span className="text-xs font-semibold">Aggregating commercial reports & database...</span>
        </div>
      ) : (
        <>
          {/* ══════════════════════════════════════════════════════ */}
          {/* TAB 1: QUOTATIONS REPORT                              */}
          {/* ══════════════════════════════════════════════════════ */}
          {activeTab === 'quotations' && (
            <div className="space-y-4">
              {/* Filter Controls Bar */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 flex flex-wrap items-center gap-3 text-xs">
                <div className="w-full sm:w-72 relative">
                  <input
                    type="text"
                    placeholder="Search by quote #, query #, customer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs focus:bg-white focus:border-blue-500"
                  />
                  <span className="absolute left-2.5 top-2.5 text-slate-400 text-xs">🔍</span>
                </div>

                <div>
                  <select
                    value={selectedCompanyId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedCompanyId(val === 'all' ? 'all' : parseInt(val, 10));
                    }}
                    className="px-3 py-2 border border-slate-300 rounded-xl font-bold text-slate-700 bg-white focus:border-blue-500"
                  >
                    <option value="all">🏢 All Operating Branches (Consolidated)</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} • {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-xl font-medium text-slate-700 bg-white focus:border-blue-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div className="ml-auto text-slate-500 font-medium">
                  Showing <strong className="text-slate-900">{filteredQuotations.length}</strong> quotations
                </div>
              </div>

              {/* Quotations Table */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs divide-y divide-slate-200">
                    <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                      <tr>
                        <th className="px-4 py-3">Quotation Ref</th>
                        <th className="px-4 py-3">Date / Query</th>
                        <th className="px-4 py-3">Customer Party</th>
                        <th className="px-3 py-3 text-center">Branch</th>
                        <th className="px-4 py-3">Sales Rep</th>
                        <th className="px-3 py-3 text-right">Subtotal</th>
                        <th className="px-3 py-3 text-right">GST (18%)</th>
                        <th className="px-4 py-3 text-right">Grand Total</th>
                        <th className="px-3 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredQuotations.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-slate-400">
                            No quotations match the active criteria.
                          </td>
                        </tr>
                      ) : (
                        filteredQuotations.map((q) => {
                          const party = parties.find((p) => p.id === q.party_id);
                          const company = companies.find((c) => c.id === q.company_id);
                          const rep = reps.find((r) => r.id === q.rep_id);

                          return (
                            <tr key={q.id} className="hover:bg-slate-50/70 transition-colors">
                              <td className="px-4 py-3 font-mono font-bold text-slate-900 text-xs">
                                <div>{q.quotation_number}</div>
                                {q.query_number && (
                                  <div className="text-[10px] font-sans font-normal text-slate-400">
                                    Ref: {q.query_number}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                <div>{q.quotation_date}</div>
                                {q.query_date ? (
                                  <div className="text-[10px] font-semibold text-blue-600 mt-0.5">
                                    Qry: {q.query_date}
                                  </div>
                                ) : (
                                  <div className="text-[10px] text-slate-400 italic">No Qry Date</div>
                                )}
                              </td>
                              <td className="px-4 py-3 font-bold text-slate-800 max-w-[220px] truncate">
                                <div>{party?.name || 'Customer'}</div>
                                {party?.address3 && (
                                  <div className="text-[10px] font-normal text-slate-400">{party.address3}</div>
                                )}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span className="font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                  {company?.code || 'SKI'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600 font-medium">
                                {rep?.name || rep?.code || '-'}
                              </td>
                              <td className="px-3 py-3 text-right text-slate-600">
                                ₹{(q.subtotal || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-3 py-3 text-right text-slate-600">
                                ₹{(q.gst_amount || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-3 text-right font-extrabold text-slate-900 text-xs">
                                ₹{(q.total || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span
                                  className={`px-2 py-0.5 text-[10px] rounded-full font-bold uppercase border ${
                                    q.status === 'approved'
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                      : q.status === 'sent'
                                      ? 'bg-blue-50 text-blue-800 border-blue-200'
                                      : 'bg-slate-100 text-slate-700 border-slate-200'
                                  }`}
                                >
                                  {q.status || 'draft'}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════ */}
          {/* TAB 2: FINANCIAL REPORTS                              */}
          {/* ══════════════════════════════════════════════════════ */}
          {activeTab === 'financial' && (
            <div className="space-y-6">
              {/* Financial KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                <KPICard title="Total Pipeline" value={`₹${financialData.totalValue.toLocaleString('en-IN')}`} sub="All filtered quotes" icon="💼" color="blue" />
                <KPICard title="Gross Subtotal" value={`₹${financialData.totalSubtotal.toLocaleString('en-IN')}`} sub="Before tax & disc" icon="📊" color="slate" />
                <KPICard title="Discounts Given" value={`₹${financialData.totalDiscount.toLocaleString('en-IN')}`} sub="Commercial cuts" icon="🏷️" color="amber" />
                <KPICard title="P&F Charges" value={`₹${financialData.totalPF.toLocaleString('en-IN')}`} sub="Packing & forwarding" icon="📦" color="slate" />
                <KPICard title="Output GST" value={`₹${financialData.totalGST.toLocaleString('en-IN')}`} sub="18% Tax Liability" icon="🏛️" color="purple" />
                <KPICard title="Approved Realized" value={`₹${financialData.approvedValue.toLocaleString('en-IN')}`} sub={`${financialData.approvedCount} approved`} icon="✅" color="emerald" />
              </div>

              {/* Branch Financial Breakdown Table */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Branch & Operating Entity Financial Ledger</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Commercial revenue, tax liability, and average ticket size per entity</p>
                  </div>
                  <button
                    onClick={() => handleExportFinancials('excel')}
                    className="text-xs font-bold text-emerald-600 hover:text-emerald-800 flex items-center gap-1 cursor-pointer"
                  >
                    <span>📥</span> Export Ledger (Excel)
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs divide-y divide-slate-200">
                    <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                      <tr>
                        <th className="px-3 py-2.5">Branch Code</th>
                        <th className="px-4 py-2.5">Entity Name</th>
                        <th className="px-3 py-2.5 text-center">Quotes</th>
                        <th className="px-3 py-2.5 text-right">Gross Subtotal</th>
                        <th className="px-3 py-2.5 text-right">Discounts</th>
                        <th className="px-3 py-2.5 text-right">Output GST</th>
                        <th className="px-4 py-2.5 text-right">Net Total</th>
                        <th className="px-3 py-2.5 text-right">Avg Ticket</th>
                        <th className="px-3 py-2.5 text-center">Pipeline Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {financialData.branches.map((b) => (
                        <tr key={b.id} className="hover:bg-slate-50/70 transition-colors">
                          <td className="px-3 py-2.5 font-mono font-bold text-slate-900">
                            <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                              {b.code}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-bold text-slate-800">{b.name}</td>
                          <td className="px-3 py-2.5 text-center font-semibold text-slate-700">{b.count}</td>
                          <td className="px-3 py-2.5 text-right text-slate-600">₹{b.grossSubtotal.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-right text-amber-600">₹{b.discountAmount.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-right text-purple-600">₹{b.gstAmount.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2.5 text-right font-extrabold text-slate-900">₹{b.netTotal.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-right text-slate-600">₹{b.avgTicket.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-center font-bold text-slate-700">{b.sharePct.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Monthly Financial Distribution */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 space-y-4">
                <div className="border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-bold text-slate-900">Monthly Revenue & Tax Distribution (Last 6 Months)</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Commercial pipeline trajectory, approved revenue realization, and tax liability</p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {financialData.monthly.map((m) => (
                    <div key={m.month} className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-black text-slate-900 uppercase">{m.month}</span>
                        <span className="text-[10px] text-slate-500 font-semibold">{m.count} quotes</span>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-400">Pipeline</div>
                        <div className="text-xs font-bold text-slate-800">₹{(m.pipelineAmount / 1000).toFixed(1)}K</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-emerald-600">Approved</div>
                        <div className="text-xs font-bold text-emerald-700">₹{(m.approvedAmount / 1000).toFixed(1)}K</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-purple-600">GST Output</div>
                        <div className="text-xs font-bold text-purple-700">₹{(m.gstAmount / 1000).toFixed(1)}K</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════ */}
          {/* TAB 3: ANALYTICS & LEADERBOARD                        */}
          {/* ══════════════════════════════════════════════════════ */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Two Column Grid: Rep Leaderboard & Funnel */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sales Rep Leaderboard (2 cols) */}
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 border-b border-slate-100 pb-3">
                    <div>
                      <h2 className="text-sm font-bold text-slate-900">Sales Representative Conversion Leaderboard</h2>
                      <p className="text-xs text-slate-500 mt-0.5">Quotations generated, status breakdown, and conversion productivity</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="hidden sm:flex items-center gap-2 text-[10px] text-slate-500">
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-400 inline-block" /> Draft</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500 inline-block" /> Sent</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" /> Approved</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500 inline-block" /> Rejected</span>
                      </div>
                      <button
                        onClick={() => handleExportAnalytics('excel')}
                        className="text-xs font-bold text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        Export Reps →
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs divide-y divide-slate-200">
                      <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                        <tr>
                          <th className="px-3 py-2.5">Representative</th>
                          <th className="px-2.5 py-2.5 text-center" title="Total Quotations">Total</th>
                          <th className="px-2 py-2.5 text-center text-slate-600" title="Draft Quotations">Draft</th>
                          <th className="px-2 py-2.5 text-center text-blue-600" title="Sent Quotations">Sent</th>
                          <th className="px-2 py-2.5 text-center text-emerald-600" title="Approved Quotations">Approved</th>
                          <th className="px-2 py-2.5 text-center text-rose-600" title="Rejected Quotations">Rejected</th>
                          <th className="px-3 py-2.5 text-right">Pipeline</th>
                          <th className="px-3 py-2.5 text-right">Realized</th>
                          <th className="px-3 py-2.5 text-center">Win Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {analyticsData.reps.map((r) => (
                          <tr key={r.id} className="hover:bg-slate-50/70 transition-colors">
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="h-7 w-7 rounded-full bg-blue-100 text-blue-800 font-bold text-[10px] flex items-center justify-center flex-shrink-0">
                                  {r.code}
                                </div>
                                <div>
                                  <div className="font-bold text-slate-900">{r.name}</div>
                                  <div className="text-[10px] text-slate-400">{r.role || 'Sales Rep'}</div>
                                </div>
                              </div>
                              {r.count > 0 && (
                                <div className="w-28 bg-slate-100 h-1.5 rounded-full overflow-hidden flex mt-1.5" title={`Draft: ${r.draftCount} | Sent: ${r.sentCount} | Approved: ${r.approvedCount} | Rejected: ${r.rejectedCount}`}>
                                  {r.draftCount > 0 && <div style={{ width: `${(r.draftCount / r.count) * 100}%` }} className="bg-slate-400 h-full" />}
                                  {r.sentCount > 0 && <div style={{ width: `${(r.sentCount / r.count) * 100}%` }} className="bg-blue-500 h-full" />}
                                  {r.approvedCount > 0 && <div style={{ width: `${(r.approvedCount / r.count) * 100}%` }} className="bg-emerald-500 h-full" />}
                                  {r.rejectedCount > 0 && <div style={{ width: `${(r.rejectedCount / r.count) * 100}%` }} className="bg-rose-500 h-full" />}
                                </div>
                              )}
                            </td>
                            <td className="px-2.5 py-2.5 text-center font-extrabold text-slate-800">{r.count}</td>
                            <td className="px-2 py-2.5 text-center">
                              <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-700">
                                {r.draftCount}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 text-center">
                              <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/60">
                                {r.sentCount}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 text-center">
                              <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                                {r.approvedCount}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 text-center">
                              <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200/60">
                                {r.rejectedCount}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right font-semibold text-slate-800">
                              ₹{r.totalAmount.toLocaleString('en-IN')}
                            </td>
                            <td className="px-3 py-2.5 text-right font-extrabold text-emerald-700">
                              ₹{r.approvedAmount.toLocaleString('en-IN')}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                                r.conversionRate >= 50
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : r.conversionRate >= 25
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-slate-100 text-slate-700'
                              }`}>
                                {r.conversionRate.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {analyticsData.reps.length > 0 && (
                        <tfoot className="bg-slate-50 font-bold text-slate-800 border-t-2 border-slate-200">
                          <tr>
                            <td className="px-3 py-2.5 text-slate-900 font-extrabold">Total / Team Average</td>
                            <td className="px-2.5 py-2.5 text-center font-extrabold text-slate-900">
                              {analyticsData.reps.reduce((s, r) => s + r.count, 0)}
                            </td>
                            <td className="px-2 py-2.5 text-center text-slate-700">
                              {analyticsData.reps.reduce((s, r) => s + r.draftCount, 0)}
                            </td>
                            <td className="px-2 py-2.5 text-center text-blue-700">
                              {analyticsData.reps.reduce((s, r) => s + r.sentCount, 0)}
                            </td>
                            <td className="px-2 py-2.5 text-center text-emerald-700">
                              {analyticsData.reps.reduce((s, r) => s + r.approvedCount, 0)}
                            </td>
                            <td className="px-2 py-2.5 text-center text-rose-700">
                              {analyticsData.reps.reduce((s, r) => s + r.rejectedCount, 0)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-slate-900">
                              ₹{analyticsData.reps.reduce((s, r) => s + r.totalAmount, 0).toLocaleString('en-IN')}
                            </td>
                            <td className="px-3 py-2.5 text-right text-emerald-700">
                              ₹{analyticsData.reps.reduce((s, r) => s + r.approvedAmount, 0).toLocaleString('en-IN')}
                            </td>
                            <td className="px-3 py-2.5 text-center text-slate-900">
                              {analyticsData.reps.reduce((s, r) => s + r.count, 0) > 0
                                ? ((analyticsData.reps.reduce((s, r) => s + r.approvedCount, 0) / analyticsData.reps.reduce((s, r) => s + r.count, 0)) * 100).toFixed(1)
                                : 0}%
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Quotation Status Funnel */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 space-y-4">
                  <div className="border-b border-slate-100 pb-3">
                    <h2 className="text-sm font-bold text-slate-900">Quotation Status Funnel</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Commercial pipeline conversion</p>
                  </div>

                  <div className="space-y-3 pt-2">
                    {analyticsData.funnel.map((f) => {
                      const colorMap: Record<string, string> = {
                        draft: 'bg-amber-500',
                        sent: 'bg-blue-500',
                        approved: 'bg-emerald-500',
                        rejected: 'bg-rose-500',
                      };
                      const barColor = colorMap[f.status] || 'bg-slate-400';

                      return (
                        <div key={f.status} className="space-y-1">
                          <div className="flex justify-between text-xs font-semibold">
                            <span className="uppercase text-slate-700 font-bold">{f.status}</span>
                            <span className="text-slate-900">
                              {f.count} quotes • ₹{(f.amount / 1000).toFixed(0)}K ({f.percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                            <div
                              className={`${barColor} h-2.5 rounded-full transition-all duration-500`}
                              style={{ width: `${Math.min(f.percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Top Customer Accounts Cards & Concentration */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Top 10 Client Accounts by Order Volume</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Highest volume accounts driving company turnover</p>
                  </div>
                  <button
                    onClick={() => handleExportAnalytics('excel')}
                    className="text-xs font-bold text-purple-600 hover:text-purple-800 cursor-pointer"
                  >
                    Export Accounts →
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {analyticsData.parties.slice(0, 5).map((p) => (
                    <div key={p.id} className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1.5 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                          Rank #{p.rank}
                        </span>
                        <span className="text-[10px] text-slate-500">{p.count} quotes</span>
                      </div>
                      <h4 className="text-xs font-bold text-slate-900 truncate" title={p.name}>{p.name}</h4>
                      <div className="text-sm font-black text-slate-900">₹{(p.amount / 1000).toFixed(1)}K</div>
                      <div className="text-[10px] text-slate-400">Avg ticket: ₹{(p.avgTicket / 1000).toFixed(1)}K</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPICard({
  title,
  value,
  sub,
  icon,
  color = 'blue',
}: {
  title: string;
  value: string;
  sub: string;
  icon: string;
  color?: 'blue' | 'emerald' | 'amber' | 'purple' | 'slate';
}) {
  const borderMap = {
    blue: 'border-blue-200/80',
    emerald: 'border-emerald-200/80',
    amber: 'border-amber-200/80',
    purple: 'border-purple-200/80',
    slate: 'border-slate-200/80',
  };

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border ${borderMap[color]} space-y-1.5`}>
      <div className="flex justify-between items-center text-slate-400">
        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">{title}</span>
        <span className="text-base">{icon}</span>
      </div>
      <div className="text-lg font-black text-slate-900 tracking-tight">{value}</div>
      <div className="text-[10px] text-slate-400 truncate">{sub}</div>
    </div>
  );
}
